'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { 
  Palette, 
  Wand2,
  Copy,
  CheckCircle,
  Save,
  Download,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react'
import { Brand } from '../../utils/mockData'
import { copyToClipboard } from '../../utils/clipboard'
import { ToolboxComments } from './ToolboxComments'
import { AISuggestions } from '../ai/AISuggestions'

interface ColorPaletteGeneratorProps {
  brand: Brand
}

interface ColorData {
  primary: string[]
  secondary: string[]
  accent: string[]
  neutral: string[]
  custom: Array<{
    name: string
    hex: string
    usage: string
  }>
}

interface ColorScheme {
  name: string
  colors: string[]
  description: string
}

const SUGGESTED_COLOR_SCHEMES: ColorScheme[] = [
  {
    name: 'Professional Blue',
    colors: ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe'],
    description: 'Trust, reliability, and professionalism'
  },
  {
    name: 'Modern Purple',
    colors: ['#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ede9fe'],
    description: 'Innovation, creativity, and luxury'
  },
  {
    name: 'Natural Green',
    colors: ['#059669', '#10b981', '#34d399', '#6ee7b7', '#d1fae5'],
    description: 'Growth, nature, and sustainability'
  },
  {
    name: 'Vibrant Orange',
    colors: ['#ea580c', '#f97316', '#fb923c', '#fdba74', '#fed7aa'],
    description: 'Energy, enthusiasm, and warmth'
  },
  {
    name: 'Elegant Gray',
    colors: ['#374151', '#6b7280', '#9ca3af', '#d1d5db', '#f3f4f6'],
    description: 'Sophistication, balance, and timelessness'
  }
]

const COLOR_CATEGORIES = [
  { key: 'primary', label: 'Primary Colors', description: 'Main brand colors for logos and primary elements' },
  { key: 'secondary', label: 'Secondary Colors', description: 'Supporting colors for backgrounds and accents' },
  { key: 'accent', label: 'Accent Colors', description: 'Highlight colors for CTAs and important elements' },
  { key: 'neutral', label: 'Neutral Colors', description: 'Text, borders, and background colors' }
]

export function ColorPaletteGenerator({ brand }: ColorPaletteGeneratorProps) {
  const [colorData, setColorData] = useState<ColorData>({
    primary: ['#2563eb'],
    secondary: ['#3b82f6'],
    accent: ['#f59e0b'],
    neutral: ['#6b7280', '#d1d5db'],
    custom: []
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [copiedColor, setCopiedColor] = useState<string | null>(null)
  const [newCustomColor, setNewCustomColor] = useState({ name: '', hex: '#000000', usage: '' })

  useEffect(() => {
    loadColorData()
  }, [brand.id])

  const loadColorData = async () => {
    setIsLoading(true)
    try {
      // Mock loading from storage - replace with actual data loading
      const savedData = localStorage.getItem(`colorPalette_${brand.id}`)
      if (savedData) {
        setColorData(JSON.parse(savedData))
      }
    } catch (error) {
      console.error('Error loading color data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveColorData = async () => {
    setIsSaving(true)
    try {
      // Mock saving to storage - replace with actual data saving
      localStorage.setItem(`colorPalette_${brand.id}`, JSON.stringify(colorData))
      setLastSaved(new Date())
    } catch (error) {
      console.error('Error saving color data:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const addColor = (category: keyof Omit<ColorData, 'custom'>, color: string) => {
    setColorData(prev => ({
      ...prev,
      [category]: [...prev[category], color]
    }))
  }

  const removeColor = (category: keyof Omit<ColorData, 'custom'>, index: number) => {
    setColorData(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }))
  }

  const updateColor = (category: keyof Omit<ColorData, 'custom'>, index: number, newColor: string) => {
    setColorData(prev => ({
      ...prev,
      [category]: prev[category].map((color, i) => i === index ? newColor : color)
    }))
  }

  const addCustomColor = () => {
    if (!newCustomColor.name.trim() || !newCustomColor.hex) return
    
    setColorData(prev => ({
      ...prev,
      custom: [...prev.custom, { ...newCustomColor }]
    }))
    setNewCustomColor({ name: '', hex: '#000000', usage: '' })
  }

  const removeCustomColor = (index: number) => {
    setColorData(prev => ({
      ...prev,
      custom: prev.custom.filter((_, i) => i !== index)
    }))
  }

  const copyColorToClipboard = async (color: string) => {
    try {
      const success = await copyToClipboard(color, false) // Don't show toast notification
      if (success) {
        setCopiedColor(color)
        setTimeout(() => setCopiedColor(null), 2000)
      }
    } catch (error) {
      console.error('Failed to copy color:', error)
    }
  }

  const applyColorScheme = (scheme: ColorScheme) => {
    setColorData(prev => ({
      ...prev,
      primary: [scheme.colors[0]],
      secondary: [scheme.colors[1]],
      accent: [scheme.colors[2]],
      neutral: [scheme.colors[3], scheme.colors[4]]
    }))
  }

  const generateAIColors = async () => {
    setIsLoading(true)
    try {
      // Mock AI color generation based on brand industry
      const industryColorMap: Record<string, string[]> = {
        'Technology': ['#2563eb', '#3b82f6', '#1d4ed8', '#60a5fa', '#93c5fd'],
        'Healthcare': ['#059669', '#10b981', '#047857', '#34d399', '#6ee7b7'],
        'Finance': ['#1e40af', '#2563eb', '#1d4ed8', '#3b82f6', '#60a5fa'],
        'Food & Beverage': ['#ea580c', '#f97316', '#c2410c', '#fb923c', '#fdba74'],
        'Fashion': ['#be185d', '#e91e63', '#a21caf', '#f472b6', '#fbb6ce'],
        'Education': ['#7c3aed', '#8b5cf6', '#6d28d9', '#a78bfa', '#c4b5fd']
      }

      const industry = brand.metadata?.industry || 'Technology'
      const suggestedColors = industryColorMap[industry] || industryColorMap['Technology']

      setColorData(prev => ({
        ...prev,
        primary: [suggestedColors[0]],
        secondary: [suggestedColors[1]],
        accent: [suggestedColors[2]],
        neutral: [suggestedColors[3], suggestedColors[4]]
      }))
    } catch (error) {
      console.error('Error generating AI colors:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const exportPalette = () => {
    const paletteData = {
      brand: brand.name,
      colors: colorData,
      exported: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(paletteData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${brand.name.toLowerCase().replace(/\s+/g, '-')}-color-palette.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const getContrastRatio = (color: string) => {
    // Simple contrast calculation - in a real app, use a proper color library
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.5 ? 'dark' : 'light'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 bg-blue-600 rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-slate-600">Loading color palette...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Color Palette Generator</h2>
          <p className="text-slate-600">Create and manage your brand's color system</p>
        </div>
        <div className="flex items-center space-x-3">
          {lastSaved && (
            <div className="flex items-center space-x-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>Saved {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
          <Button 
            variant="outline"
            onClick={exportPalette}
            size="sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            onClick={saveColorData}
            disabled={isSaving}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Palette'}
          </Button>
        </div>
      </div>

      {/* AI Color Generation */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5" />
                AI Color Suggestions
              </CardTitle>
              <CardDescription>
                Get AI-powered color recommendations based on your industry and brand
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3">
                <Button 
                  onClick={generateAIColors}
                  disabled={isLoading}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  {isLoading ? 'Generating...' : 'Generate AI Colors'}
                </Button>
                <span className="text-sm text-slate-600">
                  Based on {brand.metadata?.industry || 'Technology'} industry
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SUGGESTED_COLOR_SCHEMES.map((scheme, index) => (
                  <Card key={index} className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => applyColorScheme(scheme)}>
                    <CardContent className="p-4">
                      <div className="flex space-x-1 mb-3">
                        {scheme.colors.map((color, colorIndex) => (
                          <div
                            key={colorIndex}
                            className="w-8 h-8 rounded"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <h4 className="font-medium text-sm">{scheme.name}</h4>
                      <p className="text-xs text-slate-600">{scheme.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div>
          <AISuggestions 
            type="colors"
            brandData={{ 
              name: brand.name, 
              industry: brand.metadata?.industry,
              colors: [...colorData.primary, ...colorData.secondary]
            }}
            currentData={colorData}
            onApplySuggestion={(suggestion) => {
              if (suggestion.value && Array.isArray(suggestion.value)) {
                setColorData(prev => ({
                  ...prev,
                  primary: [suggestion.value[0]],
                  secondary: [suggestion.value[1]],
                  accent: suggestion.value.length > 2 ? [suggestion.value[2]] : prev.accent
                }))
              }
            }}
            compact={true}
          />
        </div>
      </div>

      {/* Color Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {COLOR_CATEGORIES.map((category) => (
          <Card key={category.key}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                {category.label}
              </CardTitle>
              <CardDescription>
                {category.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {colorData[category.key as keyof Omit<ColorData, 'custom'>].map((color, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-slate-200 cursor-pointer relative group"
                        style={{ backgroundColor: color }}
                        onClick={() => copyColorToClipboard(color)}
                      >
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg flex items-center justify-center transition-all">
                          {copiedColor === color ? (
                            <CheckCircle className="w-4 h-4 text-white" />
                          ) : (
                            <Copy className="w-4 h-4 text-white opacity-0 group-hover:opacity-100" />
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <Input
                          value={color}
                          onChange={(e) => updateColor(category.key as keyof Omit<ColorData, 'custom'>, index, e.target.value)}
                          className="h-8 text-xs font-mono"
                        />
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-slate-500">
                            {getContrastRatio(color) === 'light' ? 'Light' : 'Dark'}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeColor(category.key as keyof Omit<ColorData, 'custom'>, index)}
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => addColor(category.key as keyof Omit<ColorData, 'custom'>, '#000000')}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Color
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Custom Colors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Custom Colors
          </CardTitle>
          <CardDescription>
            Add custom colors with specific names and usage guidelines
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-2">
              <Label htmlFor="colorName">Color Name</Label>
              <Input
                id="colorName"
                placeholder="e.g., Brand Red"
                value={newCustomColor.name}
                onChange={(e) => setNewCustomColor(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="colorHex">Hex Code</Label>
              <div className="flex space-x-2">
                <input
                  type="color"
                  value={newCustomColor.hex}
                  onChange={(e) => setNewCustomColor(prev => ({ ...prev, hex: e.target.value }))}
                  className="w-12 h-10 rounded border-2 border-slate-200"
                />
                <Input
                  value={newCustomColor.hex}
                  onChange={(e) => setNewCustomColor(prev => ({ ...prev, hex: e.target.value }))}
                  className="font-mono"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="colorUsage">Usage</Label>
              <Input
                id="colorUsage"
                placeholder="e.g., Buttons, Links"
                value={newCustomColor.usage}
                onChange={(e) => setNewCustomColor(prev => ({ ...prev, usage: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button onClick={addCustomColor} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          </div>

          {colorData.custom.length > 0 && (
            <div className="space-y-2">
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {colorData.custom.map((color, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 border rounded-lg">
                    <div
                      className="w-10 h-10 rounded border-2 border-slate-200 cursor-pointer"
                      style={{ backgroundColor: color.hex }}
                      onClick={() => copyColorToClipboard(color.hex)}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{color.name}</div>
                      <div className="text-xs text-slate-600">{color.hex}</div>
                      {color.usage && (
                        <div className="text-xs text-slate-500">Usage: {color.usage}</div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeCustomColor(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Color Accessibility */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Accessibility Check
          </CardTitle>
          <CardDescription>
            Ensure your colors meet accessibility standards
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Primary Color Combinations</h4>
              <div className="space-y-2">
                {colorData.primary.map((primaryColor, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center space-x-3">
                      <div className="flex">
                        <div
                          className="w-8 h-8 rounded-l"
                          style={{ backgroundColor: primaryColor }}
                        />
                        <div className="w-8 h-8 rounded-r bg-white border border-l-0" />
                      </div>
                      <span className="text-sm font-mono">{primaryColor}</span>
                    </div>
                    <Badge variant={getContrastRatio(primaryColor) === 'dark' ? 'default' : 'secondary'}>
                      {getContrastRatio(primaryColor) === 'dark' ? 'Good Contrast' : 'Check Contrast'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-3">Tips for Better Accessibility</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Ensure 4.5:1 contrast ratio for normal text</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Use 3:1 contrast ratio for large text</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Test with color blindness simulators</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Don't rely on color alone to convey information</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <ToolboxComments 
        brandId={brand.id} 
        toolboxItemId="color-palette" 
        toolboxItemTitle="Color Palette Generator" 
      />
    </div>
  )
}